import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';

/**
 * Timezone utility functions to handle date operations in user's timezone
 */

/**
 * Get the current date in user's timezone as YYYY-MM-DD format
 */
export const getCurrentDateInTimezone = (timezone: string = 'UTC'): string => {
  const now = new Date();
  return formatInTimeZone(now, timezone, 'yyyy-MM-dd');
};

/**
 * Get the start of day in user's timezone
 */
export const getStartOfDayInTimezone = (date: Date, timezone: string = 'UTC'): Date => {
  const zonedDate = toZonedTime(date, timezone);
  zonedDate.setHours(0, 0, 0, 0);
  return fromZonedTime(zonedDate, timezone);
};

/**
 * Get the end of day in user's timezone
 */
export const getEndOfDayInTimezone = (date: Date, timezone: string = 'UTC'): Date => {
  const zonedDate = toZonedTime(date, timezone);
  zonedDate.setHours(23, 59, 59, 999);
  return fromZonedTime(zonedDate, timezone);
};

/**
 * Convert a date to user's timezone
 */
export const toUserTimezone = (date: Date, timezone: string = 'UTC'): Date => {
  return toZonedTime(date, timezone);
};

/**
 * Convert a date from user's timezone to UTC
 */
export const fromUserTimezone = (date: Date, timezone: string = 'UTC'): Date => {
  return fromZonedTime(date, timezone);
};

/**
 * Format a date in user's timezone
 */
export const formatDateInTimezone = (date: Date, format: string, timezone: string = 'UTC'): string => {
  return formatInTimeZone(date, timezone, format);
};

/**
 * Check if a date is today in user's timezone
 */
export const isToday = (date: Date, timezone: string = 'UTC'): boolean => {
  const today = getCurrentDateInTimezone(timezone);
  const dateStr = formatInTimeZone(date, timezone, 'yyyy-MM-dd');
  return today === dateStr;
};

/**
 * Get date relative to today in user's timezone (e.g., -1 for yesterday, 1 for tomorrow)
 */
export const getRelativeDateInTimezone = (daysOffset: number, timezone: string = 'UTC'): string => {
  const now = new Date();
  const zonedNow = toZonedTime(now, timezone);
  zonedNow.setDate(zonedNow.getDate() + daysOffset);
  return formatInTimeZone(fromUserTimezone(zonedNow, timezone), timezone, 'yyyy-MM-dd');
};

/**
 * Get the user's current timezone from browser or fallback
 */
export const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};